import React, { useState, useEffect } from 'react';
import { FileText, Image, Loader2, AlertCircle, Eye, RotateCcw, RotateCw } from 'lucide-react';
import { fetchFilePreview } from './DocumentViewer';

const DocumentPreview = ({ dossierId, file, rotation = 0, onRotate, onOpen }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!dossierId || !file?.file_id) {
        setError('Fichier invalide');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setPreview(await fetchFilePreview(dossierId, file.file_id));
        setError(null);
      } catch (err) {
        console.error('Erreur chargement preview:', err);
        setError(err.response?.data?.detail || 'Impossible de charger le fichier');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [dossierId, file?.file_id]);

  const isImage = preview?.content_type?.startsWith('image/');
  const isPDF = preview?.content_type === 'application/pdf';
  // Backend returns "content", not "data"
  const previewData = preview?.content || preview?.data;

  if (loading) {
    return (
      <div className="border rounded-lg p-4 bg-gray-50 flex flex-col items-center justify-center h-48">
        <Loader2 className="animate-spin text-blue-500 mb-2" size={24} />
        <span className="text-sm text-gray-500">Chargement...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-lg p-4 bg-red-50 flex flex-col items-center justify-center h-48">
        <AlertCircle className="text-red-500 mb-2" size={24} />
        <span className="text-sm text-red-600">{error}</span>
        <span className="text-xs text-gray-500 mt-1">{file?.filename}</span>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
      {/* Filename header */}
      <div className="px-3 py-2 bg-gray-100 border-b flex items-center gap-2">
        {isImage ? (
          <Image size={16} className="text-green-600" />
        ) : (
          <FileText size={16} className="text-blue-600" />
        )}
        <span className="text-sm font-medium text-gray-700 truncate" title={file?.filename}>
          {file?.filename}
        </span>
      </div>

      {/* Preview content */}
      <div className="p-2">
        {isImage && previewData && (
          <>
            <div className="relative w-full aspect-square rounded overflow-hidden bg-gray-50 group">
              <img
                src={`data:${preview.content_type};base64,${previewData}`}
                alt={file?.filename}
                className="absolute inset-0 w-full h-full object-contain cursor-pointer transition-transform duration-150"
                style={{ transform: `rotate(${rotation}deg)` }}
                onClick={onOpen}
              />
              {onRotate && (
                <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRotate(((rotation - 90) + 360) % 360);
                    }}
                    className="p-1.5 rounded bg-black/60 text-white hover:bg-black/80"
                    title="Pivoter à gauche"
                    aria-label="Pivoter à gauche"
                  >
                    <RotateCcw size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRotate((rotation + 90) % 360);
                    }}
                    className="p-1.5 rounded bg-black/60 text-white hover:bg-black/80"
                    title="Pivoter à droite"
                    aria-label="Pivoter à droite"
                  >
                    <RotateCw size={14} />
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onOpen}
              className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <Eye size={14} />
              Agrandir
            </button>
          </>
        )}

        {isPDF && (
          <div className="aspect-square flex flex-col items-center justify-center bg-gray-50">
            <FileText size={48} className="text-red-500 mb-3" />
            <button
              onClick={onOpen}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Eye size={16} />
              Ouvrir le PDF
            </button>
          </div>
        )}

        {!isImage && !isPDF && (
          <div className="aspect-square flex flex-col items-center justify-center bg-gray-50">
            <FileText size={48} className="text-gray-400 mb-2" />
            <span className="text-sm text-gray-500">
              Type: {preview?.content_type || 'Inconnu'}
            </span>
          </div>
        )}
      </div>

    </div>
  );
};

export default DocumentPreview;
